/**
 * Client for the device status summary (tri-server#1364) —
 * POST /customers/:customer/assets/status-summary.
 *
 * One call returns status, the newest pressure / temperature / battery reading, the last
 * uplink and the next check for a list of devices, so a floor plan or a table page needs one
 * request instead of one per device. Unknown or foreign ids are silently left out.
 */

import { configuration } from "./Configuration";
import { ApplicationError, UnauthorizedError } from "./general/Errors";
import { authenticatedFetch } from "./JWTAuthService";
import type { AssetStatus } from "./utilities/assetStatus";

/** The server rejects more ids than this per request. */
export const ASSET_STATUS_SUMMARY_MAX_IDS = 500;

export interface AssetReading {
  value: number;
  unit: string;
  timestamp: string;
}

export interface AssetStatusSummary {
  /** Mongo `_id` (hex). */
  asset: string;
  /** Business id (the number on the device). */
  id: string;
  status: AssetStatus;
  /** Short machine key such as "lowPressure" or "noUplinkSince"; optional. */
  statusReason?: string | null;
  statusTimestamp?: string | null;
  lastUplinkAt?: string | null;
  readings: {
    pressure?: AssetReading;
    temperature?: AssetReading;
    batteryVoltage?: AssetReading;
    batteryStateOfCharge?: AssetReading;
  };
  nextCheck?: string | null;
  /**
   * Data is not current: no uplink, or the last one older than three send intervals — also for
   * critical/warning, which keep their status. Optional until every server sends it.
   */
  stale?: boolean;
  /** The send interval the server's freshness rules used. */
  sendIntervalSeconds?: number;
}

const processResponse = async (response: Response) => {
  if (response.ok) {
    return response.json();
  }

  if (response.status === 401) {
    throw new UnauthorizedError();
  }

  let errorMessage = `${response.status} ${response.statusText}`;

  try {
    const errorData = await response.json();
    errorMessage = errorData.message || errorData.error || errorMessage;
  } catch {
    // response body not JSON — keep default message
  }

  throw new ApplicationError(errorMessage);
};

/**
 * Status summaries for up to {@link ASSET_STATUS_SUMMARY_MAX_IDS} devices (Mongo `_id`s and/or
 * business ids). Callers with more ids split them; `useAssetStatusSummary` does.
 */
export async function fetchAssetStatusSummaries(
  customer: string,
  assetIds: string[],
): Promise<AssetStatusSummary[]> {
  const response = await authenticatedFetch(
    `${configuration.serverUrl}/customers/${customer}/assets/status-summary`,
    {
      method: "POST",
      body: JSON.stringify({ assets: assetIds }),
      headers: { "Content-Type": "application/json" },
    },
  );

  const result = await processResponse(response);

  return Array.isArray(result?.assets) ? result.assets : [];
}
